import React from 'react';
import { Column } from '@tanstack/react-table';
import { CalendarIcon } from 'lucide-react';
import { formatDate } from 'utils/custom-date';
import { useIsMobile } from 'hooks/use-mobile';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import usePopoverWidth from 'hooks/use-popover-width';
import { Calendar } from 'components/ui/calendar';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';

/** Start of the given calendar day in the user's local timezone. */
function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** End of the given calendar day in the user's local timezone (inclusive range). */
function endOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/**
 * Normalizes a react-day-picker range to standard filter semantics:
 * - First day = inclusive start at local 00:00:00
 * - Last day = inclusive end at local 23:59:59.999
 * - If the user selects end before start, dates are swapped (start-to-end behavior)
 */
function normalizeDateRangeForFilter(range: DateRange | undefined): DateRange | undefined {
  if (!range?.from) return undefined;

  let startDay = new Date(range.from);
  let endDay = range.to ? new Date(range.to) : undefined;

  if (endDay) {
    const startKey = startOfLocalDay(startDay).getTime();
    const endKey = startOfLocalDay(endDay).getTime();
    if (startKey > endKey) {
      const tmp = startDay;
      startDay = endDay;
      endDay = tmp;
    }
    return {
      from: startOfLocalDay(startDay),
      to: endOfLocalDay(endDay),
    };
  }

  return {
    from: startOfLocalDay(startDay),
    to: undefined,
  };
}

/**
 * DateRangeFilter Component
 *
 * A filter component that allows users to select a date range from a calendar.
 * This component integrates with `@tanstack/react-table` to filter data based on the selected date range.
 * It supports both desktop and mobile views, with responsive layouts and popover behavior.
 *
 * Features:
 * - Allows users to select a range of dates using a calendar UI.
 * - Displays the selected date range in the filter button.
 * - Clears the selected date range with a "Clear filter" button.
 * - Integrates with `@tanstack/react-table` to filter data based on the selected date range.
 * - Supports mobile-friendly views with responsive layouts.
 *
 * @template TData - The type of data used in the table.
 * @template TValue - The type of value for the column being filtered.
 *
 * @param {Column<TData, TValue>} [column] - The column to be filtered, passed from `@tanstack/react-table`.
 * @param {string} title - The title to be displayed for the date range filter.
 * @param {DateRange | undefined} date - The current selected date range.
 * @param {(date: DateRange | undefined) => void} onDateChange - Callback to handle changes to the selected date range.
 *
 * @returns {JSX.Element} A date range filter component.
 */

interface DateRangeFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title: string;
  date: DateRange | undefined;
  onDateChange: (date: DateRange | undefined) => void;
  triggerClassName?: string;
  forceOutline?: boolean;
  iconPosition?: 'start' | 'end';
}

export function DateRangeFilter<TData, TValue>({
  column,
  title,
  date,
  onDateChange,
  triggerClassName,
  forceOutline = false,
  iconPosition = 'start',
}: Readonly<DateRangeFilterProps<TData, TValue>>) {
  const isMobile = useIsMobile();
  const [buttonRef] = usePopoverWidth();
  const [open, setOpen] = React.useState(false);
  const [localDateRange, setLocalDateRange] = React.useState<DateRange | undefined>(date);
  const [month, setMonth] = React.useState<Date>(date?.from || new Date());
  const { t } = useTranslation();

  React.useEffect(() => {
    setLocalDateRange(date);
    if (date?.from) {
      setMonth(date.from);
    }
  }, [date]);

  const handleDateSelect = (selectedDateRange: DateRange | undefined) => {
    if (!selectedDateRange?.from) {
      setLocalDateRange(undefined);
      onDateChange(undefined);
      column?.setFilterValue(undefined);
      return;
    }

    const normalized = normalizeDateRangeForFilter(selectedDateRange);
    setLocalDateRange(normalized);
    onDateChange(normalized);
    column?.setFilterValue(normalized);
  };

  const clearFilter = (e: React.MouseEvent) => {
    e.stopPropagation();

    setLocalDateRange(undefined);
    onDateChange(undefined);
    column?.setFilterValue(undefined);
    setMonth(new Date());
    setOpen(false);
  };

  const handlePopoverKeyDown = (e: React.KeyboardEvent) => {
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.stopPropagation();
    }
  };

  const hasActiveFilter = localDateRange?.from != null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={buttonRef}
          variant={hasActiveFilter && !forceOutline ? 'default' : 'outline'}
          size="sm"
          className={cn(
            '!h-8 rounded-[1rem] border border-[#e7e7e7] dark:border-grey-400',
            hasActiveFilter && !forceOutline ? '' : 'text-primary-600 dark:text-[#ffffff]',
            triggerClassName
          )}
        >
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex min-w-0 items-center">
              {iconPosition === 'start' && (
                <CalendarIcon className="mr-2 h-4 w-4 shrink-0 dark:text-[#ffffff]" />
              )}
              <span className="truncate">{t(title)}</span>
              {localDateRange?.from && (
                <>
                  <Separator orientation="vertical" className="hidden h-4 sm:mx-2 sm:block" />
                  <span className="ml-2 truncate">
                    {formatDate(localDateRange.from, true)}
                    {localDateRange.to && (
                      <>
                        {' - '}
                        {formatDate(localDateRange.to, true)}
                      </>
                    )}
                  </span>
                </>
              )}
            </div>
            {iconPosition === 'end' && (
              <CalendarIcon className="h-5 w-5 shrink-0 text-[#3d3d3d] dark:text-[#ffffff]" />
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto max-w-[100vw] overflow-hidden p-0"
        align="start"
        sideOffset={8}
        onKeyDown={handlePopoverKeyDown}
      >
        <div
          className="flex flex-col overflow-hidden rounded-[6px] bg-popover"
          onClick={(e) => {
            e.stopPropagation();
          }}
          onKeyDown={handlePopoverKeyDown}
          role="presentation"
        >
          <Calendar
            initialFocus
            mode="range"
            month={month}
            onMonthChange={setMonth}
            defaultMonth={localDateRange?.from ?? new Date()}
            selected={localDateRange}
            onSelect={handleDateSelect}
            numberOfMonths={isMobile ? 1 : 2}
            className="rounded-none border-0 bg-popover"
            classNames={{
              caption_label: 'text-foreground',
              nav_button:
                'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-foreground',
              nav_button_next: 'absolute right-1',
              nav_button_previous: 'absolute left-1',
              day: 'h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-foreground rounded-lg hover:bg-accent hover:text-accent-foreground',
              day_selected:
                'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-lg',
              day_outside:
                'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
              day_disabled: 'text-muted-foreground opacity-50',
              day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground rounded-none',
            }}
          />
          <div className="border-t border-border bg-popover p-2">
            <Button variant="ghost" onClick={clearFilter} className="w-full" size="sm">
              {t('CLEAR_FILTER')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
